// The Upcoming view's range choices, and the arithmetic that turns one into a
// day count the range endpoints will accept.

export type RangeId = "day" | "week" | "1w" | "2w" | "10w";

export const RANGE_OPTIONS: { id: RangeId; label: string }[] = [
  { id: "day", label: "1 day" },
  { id: "week", label: "Current week" },
  { id: "1w", label: "1 week" },
  { id: "2w", label: "2 weeks" },
  { id: "10w", label: "10 weeks" },
];

/** The server's date window (`main._MAX_DAYS_AHEAD`) and its range cap
 *  (`showtime_api.MAX_RANGE_DAYS`). Both 70, kept apart because they are two
 *  limits that happen to agree. */
const MAX_DAYS_AHEAD = 70;
const MAX_RANGE_DAYS = 70;

/** Exhibition weeks run Friday to Thursday, so the current week ends Thursday. */
const THURSDAY = 4;

const MS_PER_DAY = 86_400_000;

/** UTC noon, the same anchor `dates.ts` uses, so no offset can shift the day. */
const noon = (date: string) => Date.parse(`${date}T12:00:00Z`);

/** `start` through the Thursday that closes its exhibition week, inclusive.
 *  A Thursday is its own week's last day, so that is one day. */
export function daysThroughThursday(start: string): number {
  const weekday = new Date(noon(start)).getUTCDay();
  return ((THURSDAY - weekday + 7) % 7) + 1;
}

/**
 * `days` from `start`, cut so the range never ends past today + 70 days.
 *
 * The end is fixed by TODAY, not by the start: the server refuses a range
 * whose last date falls outside its window, so "10 weeks" from a start five
 * days out would otherwise be a 400 rather than a slightly shorter range.
 * Never below 1 -- a start already past the window is the server's to refuse.
 */
export function clampDays(days: number, start: string, today: string): number {
  const toWindowEnd = Math.round((noon(today) - noon(start)) / MS_PER_DAY) + MAX_DAYS_AHEAD + 1;
  return Math.max(1, Math.min(days, MAX_RANGE_DAYS, toWindowEnd));
}

const FIXED_DAYS: Record<Exclude<RangeId, "week">, number> = {
  day: 1,
  "1w": 7,
  "2w": 14,
  "10w": 70,
};

/** How many days `range` covers from `start`, already clamped. */
export function rangeDays(range: RangeId, start: string, today: string): number {
  const wanted = range === "week" ? daysThroughThursday(start) : FIXED_DAYS[range];
  return clampDays(wanted, start, today);
}
